/**
 * "+ Evaluator": start from scratch (an LLM judge or a code check) or from a
 * template. Either way it opens the configure screen pre-filled; nothing is
 * saved until "Create" there.
 */

import { Code, Sparkles } from "lucide-react";
import { Link } from "react-router";
import { SidePanel } from "@/components/ui/overlay";
import { Pill } from "@/components/ui/pill";
import { QueryState } from "@/components/ui/states";
import { useEvaluatorTemplates } from "./hooks";
import { CATEGORY_LABEL, CATEGORY_ORDER, KIND } from "./labels";

const SCRATCH = [
  {
    kind: "llm_judge",
    title: "LLM judge",
    text: "Write a prompt that grades the answer",
    icon: Sparkles,
  },
  { kind: "code", title: "Code check", text: "A fixed rule, like exact match", icon: Code },
] as const;

const CARD =
  "flex flex-col gap-1 rounded-lg border border-line px-3.5 py-3 text-left hover:border-ink hover:bg-hover";

/** `returnTo`: a page to come back to once the evaluator is created (the new-experiment flow). */
export function CreateDrawer({ onClose, returnTo }: { onClose: () => void; returnTo?: string }) {
  const back = returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : "";
  const templates = useEvaluatorTemplates();
  const list = templates.data ?? [];
  const groups = CATEGORY_ORDER.map((c) => ({
    category: c,
    items: list.filter((t) => t.category === c),
  })).filter((g) => g.items.length > 0);

  return (
    <SidePanel
      title="New evaluator"
      subtitle="Start from scratch or from a template."
      onClose={onClose}
      width={640}
    >
      <section aria-label="Create from scratch" className="flex flex-col gap-2">
        <h3 className="text-[13px] font-semibold">Create from scratch</h3>
        <div className="grid grid-cols-2 gap-2.5">
          {SCRATCH.map((s) => (
            <Link key={s.kind} to={`/evaluators/new?kind=${s.kind}${back}`} className={CARD}>
              <span className="flex items-center gap-2 text-[13px] font-semibold">
                <s.icon size={14} strokeWidth={2} aria-hidden />
                {s.title}
              </span>
              <span className="text-xs text-muted">{s.text}</span>
            </Link>
          ))}
        </div>
      </section>
      <section aria-label="Start from a template" className="flex flex-col gap-3">
        <h3 className="text-[13px] font-semibold">Start from a template</h3>
        <QueryState query={templates} what="templates">
          {groups.map((g) => (
            <div key={g.category} className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold tracking-[0.05em] text-faint uppercase">
                {CATEGORY_LABEL[g.category]}
              </span>
              <div className="grid grid-cols-2 gap-2">
                {g.items.map((t) => (
                  <Link
                    key={t.key}
                    to={`/evaluators/new?template=${encodeURIComponent(t.key)}${back}`}
                    className={CARD}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-[13px] font-medium">{t.name}</span>
                      <Pill tone={KIND[t.kind]?.tone ?? "neutral"}>{KIND[t.kind]?.label ?? t.kind}</Pill>
                    </span>
                    <span className="line-clamp-2 text-xs text-muted">{t.description}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </QueryState>
      </section>
    </SidePanel>
  );
}
